export const useCalculateVisitNumber = (
	enrollmentId: string,
	programId?: string,
	lastVisitNumber?: number,
	treatmentConfigName?: string,
	behaviorId?: string,
	historyIndex?: number,
) => {
	return useQuery<number, Error>({
		queryKey: [
			"calculate-visit-number",
			enrollmentId,
			programId,
			lastVisitNumber,
			treatmentConfigName,
			behaviorId,
			historyIndex,
		],
		queryFn: () =>
			rewardsEngineService
				.calculateVisitNumber(
					enrollmentId,
					programId ?? "",
					treatmentConfigName,
					behaviorId,
					historyIndex,
				)
				.catch(() => 1),
		enabled: !!enrollmentId && !!programId,
		retry: false,
	});
};
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rewardsEngineService } from "../services/rewards-engine.service";
import {
	GetRewardResponse,
	RequestEnginePayload,
	RewardsEngine,
} from "../domain/reward.interface";

export const useRewardsEngine = (
	enabled = false,
	filters?: { eventType?: string; event?: string },
) => {
	return useQuery<RewardsEngine[], Error>({
		queryKey: ["get-rewards-engine", filters],
		queryFn: () => rewardsEngineService.findAll(filters),
		staleTime: 1 * 60 * 60 * 1000,
		enabled,
	});
};

export const useCreateRewardConfig = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: Partial<RewardsEngine>) => {
			const response = await rewardsEngineService.create(data);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["get-rewards-engine"] });
		},
	});
};

export const useUpdateRewardConfig = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			id,
			data,
		}: {
			id: string;
			data: Partial<RewardsEngine>;
		}) => {
			const response = await rewardsEngineService.update(id, data);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["get-rewards-engine"] });
		},
	});
};

export const useDeleteRewardConfig = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string) => {
			await rewardsEngineService.remove(id);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["get-rewards-engine"] });
		},
	});
};

export const useGetReward = () => {
	return useMutation<GetRewardResponse, Error, RequestEnginePayload>({
		mutationFn: async (payload) => {
			const response = await rewardsEngineService.getReward(payload);
			return response;
		},
	});
};
